import { useState, useEffect } from 'react';
import { saveCpcId, getCpcId } from 'service/local-data-provider';
import AppMessage from 'components/app-message';
import { getLoginState } from 'model/user';
import repo from 'service/repository';
import { INIT_APP_DONE, SHOW_TOAST_MESSAGE, UPDATE_BOOTSTRAPS } from 'use-app';
import { isAlpha, PATH } from 'config';

const useInit = () => {
  const RETRY_INTERVAL_TIMESTAMP = 500;
  const MAX_RETRY_TIMES = 3;
  const [isPending, setIsPending] = useState(true);
  const [isError, setIsError] = useState(null);

  useEffect(() => {
    let intervalId = null;
    let retryTimes = 0;
    // 設定一個timer，每 500 ms 嘗試取得跟 cpcpay app 取得 cpc id
    intervalId = setInterval(() => {
      try {
        const userAgent = window.navigator.userAgent.toLowerCase();
        if (userAgent.match(/android/) != null) {
          window.cpcpay.updateCPCId();
        } else if (userAgent.match(/i(phone|pad)/)) {
          window.webkit.messageHandlers.updateCPCId.postMessage({});
        } else {
          console.log('不支援的平台');
        }
      } catch (e) {
        console.log('無法執行 js interface 請確認是不是在 cpcpay app 內打開');
      }

      let cpcId = window.cpc_id || null;

      // FIXME 測試站需要等到嘗試到 MAX_RETRY_TIMES 才能塞測試用的 CPCID
      if (retryTimes >= MAX_RETRY_TIMES && isAlpha) {
        // cpc id for alpha env
        cpcId = window.cpc_id || '5FDCB794812042771FB762AA7D7DE910513F0FF1E658EEC60DF17AE2BB80D111';
      }

      if (cpcId !== undefined && cpcId !== null) {
        saveCpcId(cpcId);
        // 拿到 cpc id 後關掉 timer
        clearInterval(intervalId);
        setIsPending(false);
      } else {
        // 最多嘗試 MAX_RETRY_TIMES 次，還是拿不到 cpc id 就放棄
        if (retryTimes >= MAX_RETRY_TIMES) {
          clearInterval(intervalId);
          setIsError(true);
        } else {
          retryTimes += 1;
        }
      }
    }, RETRY_INTERVAL_TIMESTAMP);
  }, []);

  return {
    isPending,
    error: isError ? (
      <AppMessage autoDismiss={false} type="failure">
        中油Pay 連線異常，請關閉 APP 後再開啟麻吉付重試。
      </AppMessage>
    ) : null,
  };
};

const loginAsUser = (success, failure) => {
  return Promise.all([
    repo.fetchUserInfo(),
    repo.fetchLicensePlates(),
    repo.fetchBootstraps(),
    repo.fetchParkingRecord(),
    repo.fetchOrders(1, 100, ['parking']),
    repo.updatePushToken(getCpcId()),
    repo.agreeTermsOfTaipeiParkinglotService(),
  ])
    .then(data => {
      const payload = {
        me: data[0],
        licensePlates: data[1],
        bootstraps: data[2],
        parkingRecord: data[3],
        orders: {
          parking: data[4].data,
        },
      };

      success(payload);
    })
    .catch(e => {
      failure(e);
    });
};

const useSplash = async ({ dispatch, history, location }, callback) => {
  const waitForSplashAnimationComplete = fn => {
    return new Promise(resolve => {
      resolve(callback());
    }).then(() => {
      setTimeout(() => fn(), 2200);
    });
  };

  const initAppWithoutPayload = () => dispatch({ type: INIT_APP_DONE });

  await repo.fetchBootstraps().then(payload => {
    dispatch({
      type: UPDATE_BOOTSTRAPS,
      payload,
    });
  });

  if (getCpcId() === '') {
    return waitForSplashAnimationComplete(() => {
      dispatch({
        type: SHOW_TOAST_MESSAGE,
        payload: '無法取得你的付款方式設定，請先至中油Pay App 確認你的付款方式設定。',
      });
    });
  }

  if (location.pathname === PATH.MAPS || location.pathname === PATH.SERVICE_INSTRUCTION) {
    return waitForSplashAnimationComplete(initAppWithoutPayload);
  }

  switch (getLoginState()) {
    case 'user':
      const success = payload => {
        waitForSplashAnimationComplete(() => {
          dispatch({
            type: INIT_APP_DONE,
            payload: payload,
          });

          if (location.pathname === '/' || location.pathname === '/index.html') {
            history.replace(PATH.MAIN);
          } else {
            history.replace(`${location.pathname}${location.search}${location.hash}`);
          }
        });
      };

      const failure = () => {
        waitForSplashAnimationComplete(() => {
          initAppWithoutPayload();
          history.replace(PATH.USER_WELCOME);
        });
      };

      loginAsUser(success, failure);
      break;
    case 'skip':
      waitForSplashAnimationComplete(() => {
        initAppWithoutPayload();
        if (location.pathname === '/') {
          history.replace(PATH.MAIN);
        } else {
          history.replace(`${location.pathname}${location.search}`);
        }
      });
      break;
    default:
      waitForSplashAnimationComplete(() => {
        initAppWithoutPayload();
        if (location.pathname !== PATH.USER_LOGIN) {
          history.replace(PATH.USER_WELCOME);
        }
      });
      break;
  }
};

const methods = {
  useInit,
  useSplash,
  loginAsUser,
};

export default methods;
